import React, { useState, useEffect } from "react";
import { IProps } from "./types";
import { IImageAttributes } from "../../../types";
import {
  Image as KonvaImage,
  Transformer,
  Group,
  Rect,
  Circle,
} from "react-konva";
import {
  resizeStorageAccountName,
  containerName,
  containerNameBrands,
} from "@storage/azure-storage-blob";
import {
  getCenter,
  positionAndRotateAroundPoint,
  getXYBeforeRotation,
  getCenterWithWidthAndHeight,
  radiansToDegrees,
  rotatePoint,
  getCornerRadius,
} from "@utils/geometry";
import { IResizeValues } from "../../CommonCommandsOptions/ResizeCommandOption/types";
import { defineZIndex } from "@utils/zIndexChains";
import { getMaskAndClipping, getBlendMode, setMasks } from "@utils/render";
import Konva from "konva";
import useImage from "use-image";
import CornersAddIn from "../AddIns/CornersAddIn";
import ElementLockedAddIn from "../AddIns/ElementLockedAddIn";
import { updateSelectorIcon } from "@utils/selector";
import colors from "@assets/themes/colors";

const useCanvas = (props: IProps) => {
  //20240416 DOS-95 Add mode as query parameter for resizing. No mode = Crop
  const desiredWidth = `?w=${Math.ceil(
    props.element.attributes.width
  )}&mode=max`;
  const attributes = props.element.attributes as IImageAttributes;
  const urlBrand = `${resizeStorageAccountName}/${containerNameBrands}`;

  let srcImage =
    attributes.srcImage ?? `${props.folder}/${props.element.id}.png`;
  if (attributes.srcImage?.split("/").length === 1)
    srcImage = `${props.folder}/${srcImage}.png`;

  let urlImage = `${resizeStorageAccountName}/${containerName}/${srcImage}`;

  //To verify if imageSrc has all path
  let anonymous = true;
  if (attributes.srcImage?.startsWith("http")) {
    urlImage = attributes.srcImage;
    anonymous = false;
  }
  //if (attributes.brandImage!==undefined && attributes.srcImage==undefined)
  if (attributes.brandId !== undefined && attributes.brandId != null) {
    urlImage = `${urlBrand}/${
      attributes.brandId
    }/${attributes.brandImageType?.toLowerCase()}/${attributes.srcImage}`;
  }

  urlImage += desiredWidth; //Adding desired width for resizing.
  //20240416 DOS-95 Info: when resizing ImageOrg has the same dimensions as attributes.image
  const [imageOrg] = useImage(urlImage, anonymous ? "anonymous" : undefined);
  const [maskImages, setMaskImages] = useState<
    (HTMLImageElement | undefined)[]
  >([]);
  const [canvasBG, setCanvasBG] = useState<HTMLCanvasElement>();
  const [canvas, setCanvas] = useState<HTMLCanvasElement>();

  useEffect(() => {
    if (attributes.rawProps)
      setMasks(attributes.rawProps, props.folder, setMaskImages);
  }, []);

  useEffect(() => {
    if (!imageOrg) {
      return;
    }

    if (props.isCropping) {
      const canvasElementBG = document.createElement("canvas");
      let ctxBG = canvasElementBG.getContext("2d")!;
      const width = Math.ceil(attributes.image.width);
      const height = Math.ceil(attributes.image.height);
      canvasElementBG.width = width;
      canvasElementBG.height = height;
      if (props.element.attributes.flip) {
        const flipXValue = attributes.flip?.substr(0, 1) === "1" ? true : false;
        const flipYValue = attributes.flip?.substr(1, 2) === "1" ? true : false;
        ctxBG.translate(flipXValue ? width : 0, flipYValue ? height : 0);
        ctxBG.scale(flipXValue ? -1 : 1, flipYValue ? -1 : 1);
      }
      //ctxBG.imageSmoothingQuality = "high"; //low is the default
      ctxBG.drawImage(imageOrg, 0, 0, width, height);
      getMaskAndClipping(ctxBG, maskImages, width, height, attributes);
      setCanvasBG(canvasElementBG);
    }
    /*20240416 DOS-95 
    Resizing: scale is always 1 to 1 - won't be used to draw the image
    Cropping: scale calculation is REQUIRED > ImageOrg dimensions are different than Attributes.Image dimensions
    20240523
    Fix for 1 pixel lost: if the dimensions difference is 1px, scale = 1
    */
    const scaleX =
      Math.abs(attributes.image.width - imageOrg.width) == 1
        ? 1
        : attributes.image.width / imageOrg.width;
    const scaleY =
      Math.abs(attributes.image.height - imageOrg.height) == 1
        ? 1
        : attributes.image.height / imageOrg.height;
    const canvasElement = document.createElement("canvas");
    let ctx = canvasElement.getContext("2d");

    if (ctx) {
      /*20240523 DOS-95
    Fix for 1 pixel lost: if scale = 1, dimensions are equal to the bigger one between imageOrg and attributes.image
    */
      const width = Math.ceil(
        scaleX == 1
          ? Math.max(attributes.width!, imageOrg.width)
          : attributes.width!
      );
      const height = Math.ceil(
        scaleY == 1
          ? Math.max(attributes.height, imageOrg.height)
          : attributes.height
      );
      const pointWithOutRotation = getXYBeforeRotation(
        { x: attributes.x, y: attributes.y },
        width,
        height,
        attributes.rotation ?? 0
      );
      const x = pointWithOutRotation.x;
      const y = pointWithOutRotation.y;
      let originalX = x;
      let originalY = y;
      if (attributes.image) {
        originalX = attributes.image.x;
        originalY = attributes.image.y;
      }
      canvasElement.width = width;
      canvasElement.height = height;
      const imageInitialX = x - originalX;
      const imageInitialY = y - originalY;
      if (props.element.attributes.flip) {
        const flipXValue = attributes.flip?.substr(0, 1) === "1" ? true : false;
        const flipYValue = attributes.flip?.substr(1, 2) === "1" ? true : false;
        ctx.translate(flipXValue ? width : 0, flipYValue ? height : 0);
        ctx.scale(flipXValue ? -1 : 1, flipYValue ? -1 : 1);
      }
      ctx.imageSmoothingQuality = "high";
      /*
      20240416 DOS-95 Draw image
        https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage#specifications
        Method for Cropping: drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
        Method for resizing: drawImage(image, dx, dy)
        where 
          image is an HTMLImageElement
          sx, sy, sWidth, sHeight are coordinates and dimensions for the source rectangle
          and dx, dy, dWidth, dHeight are coordinates and dimensions for the destination canvas 
          
          When resizing, sWidth and sHeight calculations using scaleX and scaleY was causing a *chopped images* bug

          Scale calculation is REQUIRED for Cropping
      */
      //If cropping...
      if (
        Math.abs(imageInitialX) > 0 ||
        Math.abs(imageInitialY) > 0 ||
        scaleX !== 1 ||
        scaleY !== 1
      ) {
        const sX = imageInitialX / scaleX;
        const sY = imageInitialY / scaleY;
        const sWidth = Math.ceil(width / scaleX);
        const sHeight = Math.ceil(height / scaleY);
        //ctx.drawImage(imageOrg, imageInitialX / scaleX, imageInitialY / scaleY, width / scaleX, height / scaleY, 0, 0, width, height);
        ctx.drawImage(imageOrg, sX, sY, sWidth, sHeight, 0, 0, width, height);
      } else {
        ctx.drawImage(imageOrg, 0, 0);
      }
      getMaskAndClipping(ctx, maskImages, width, height, attributes);
      setCanvas(canvasElement);
    }
  }, [
    imageOrg,
    maskImages,
    attributes.flip,
    props.isCropping,
    props.isSelected,
    attributes.width,
    attributes.height,
  ]);

  return { canvas, canvasBG };
};

const ImageCanvasElement: React.FC<IProps> = (props) => {
  let attributes = props.element.attributes as IImageAttributes;
  const imageRef = React.useRef<Konva.Image>(null);
  const trRef = React.useRef<Konva.Transformer>(null);
  const cropMainGroupRef = React.useRef<Konva.Group>(null);
  const cropGroupRef = React.useRef<Konva.Group>(null);
  const cropBgImageRef = React.useRef<Konva.Image>(null);
  const cropFrontImageRef = React.useRef<Konva.Image>(null);
  const cropRectRef = React.useRef<Konva.Rect>(null);
  const cropTrRef = React.useRef<Konva.Transformer>(null);
  const lockIconRef = React.useRef<Konva.Image>(null);

  const showDescribedColor: boolean =
    props.describedLayerColor !== "" && !attributes.hidden;

  const images = useCanvas(props);
  const imageCrop = images.canvas;
  const imageBG = images.canvasBG;

  useEffect(() => {
    if (props.isSelected || props.isOver || showDescribedColor) {
      if (cropRectRef.current) {
        cropTrRef.current?.nodes([cropRectRef.current]);
        cropTrRef.current?.getLayer()?.batchDraw();
        /*DOS-79: Show Crop Transformer even when image is out of Artboard bounds
          Not sure if cropRectRef should be the 2nd parameter instead of imageRef
        */
        props.showTransfomerOutside(cropTrRef, imageRef, props.isSelected);
      }
      if (imageRef.current) {
        trRef.current?.nodes([imageRef.current]);
        trRef.current?.getLayer()?.batchDraw();
        props.showTransfomerOutside(trRef, imageRef, props.isSelected);
      }
    }
  }, [
    props.isSelected,
    props.isOver,
    props.isCropping,
    props.describedLayerColor,
    props.zoom,
  ]);

  useEffect(() => {
    if (lockIconRef.current) lockIconRef.current.visible(false);
    if (
      trRef.current &&
      props.element?.attributes &&
      (props.isSelected || props.isOver || showDescribedColor)
    )
      updateSelectorIcon(
        trRef.current,
        lockIconRef.current,
        attributes.locked === true &&
          !props.isSelectDescribe &&
          !props.isMultipleSelection
      );
  }, [
    trRef.current,
    lockIconRef.current,
    props.isOver,
    attributes.locked,
    props.isSelectDescribe,
    props.isMultipleSelection,
  ]);

  useEffect(() => {
    if (!props.isCropping) {
      defineZIndex(imageRef, props.element.id, props.zIndexChain);
    }
  }, [props.isCropping, JSON.stringify(props.zIndexChain)]);

  useEffect(() => {
    const node = imageRef.current;
    if (node) {
      node.image(imageCrop);
      node.x(attributes.x);
      node.y(attributes.y);
      node.width(attributes.width ?? 0);
      node.height(attributes.height);
    }
  }, [imageCrop, attributes.x, attributes.y, attributes.rotation]);

  const tranforImage = () => {
    const node = cropRectRef.current;
    const nodeImage = cropBgImageRef.current;

    if (node && nodeImage) {
      const scaleX = node!.scaleX();
      const scaleY = node!.scaleY();

      let newWidth = Math.ceil(Math.max(5, node!.width() * scaleX));
      let newHeight = Math.ceil(Math.max(node!.height() * scaleY));

      let rotation = node.rotation();

      node?.width(newWidth);
      node?.height(newHeight);

      let resizeValues: IResizeValues = {
        x: node.position().x,
        y: node.position().y,
        rotation: rotation ? rotation : undefined,
        width: newWidth,
        height: newHeight,
        image: {
          x: nodeImage.position().x,
          y: nodeImage.position().y,
          width: Math.ceil(nodeImage.width()),
          height: Math.ceil(nodeImage.height()),
        },
      };
      props.onResize(resizeValues);
      props.onRotation();
    }
  };

  useEffect(() => {
    applyColorOverlay();
  }, [imageCrop, imageBG, attributes.colorOverlay]);

  const applyColorOverlay = () => {
    if (attributes.colorOverlay) {
      if (imageRef && imageRef.current) {
        const node = imageRef.current;
        node.cache();
        node.filters([Konva.Filters.RGBA]);
        node.red(attributes.colorOverlay.red);
        node.green(attributes.colorOverlay.green);
        node.blue(attributes.colorOverlay.blue);
        node.alpha(attributes.colorOverlay.alpha);
      }
      if (cropFrontImageRef && cropFrontImageRef.current) {
        const node = cropFrontImageRef.current;
        node.cache();
        node.filters([Konva.Filters.RGBA]);
        node.red(attributes.colorOverlay.red);
        node.green(attributes.colorOverlay.green);
        node.blue(attributes.colorOverlay.blue);
        node.alpha(attributes.colorOverlay.alpha);
      }
    }
  };

  const cropTransformerAttibutes = () => {
    let tranformCropAtribbutes = {};
    if (props.isCropping)
      tranformCropAtribbutes = {
        anchorSize: 5,
        anchorFill: "#49d8de",
        enabledAnchors: [
          "top-left",
          "top-right",
          "bottom-left",
          "bottom-right",
        ],
        borderDash: [5, 3],
      };
    return tranformCropAtribbutes;
  };

  const cropLimits = (newBox: any) => {
    const limitsShape = cropBgImageRef.current;
    if (limitsShape && cropTrRef.current) {
      const limitsPosition = limitsShape.absolutePosition();
      const newBoxPosition = { x: newBox.x, y: newBox.y };
      const rotationCenter = getCenterWithWidthAndHeight(
        props.element.attributes.x,
        props.element.attributes.y,
        props.element.attributes.width,
        props.element.attributes.height,
        props.element.attributes.rotation ?? 0
      );
      const limitsPositionBeforeRotation = getXYBeforeRotation(
        limitsPosition,
        limitsShape.width(),
        limitsShape.height(),
        props.element.attributes.rotation ?? 0,
        rotationCenter
      );
      const newBoxBeforeRotation = getXYBeforeRotation(
        newBoxPosition,
        newBox.width,
        newBox.height,
        props.element.attributes.rotation ?? 0,
        rotationCenter
      );
      const zoomX = cropTrRef.current.getLayer()?.getStage().scaleX() ?? 1;
      const zoomY = cropTrRef.current.getLayer()?.getStage().scaleY() ?? 1;
      const limitsWidth = limitsShape.width() * zoomX;
      const limitsHeight = limitsShape.height() * zoomY;
      let newBoxWidth = newBox.width;
      let newBoxHeight = newBox.height;
      if (newBoxBeforeRotation.x < limitsPositionBeforeRotation.x) {
        newBoxWidth =
          newBoxWidth -
          (limitsPositionBeforeRotation.x - newBoxBeforeRotation.x);
        newBoxBeforeRotation.x = limitsPositionBeforeRotation.x;
      }
      if (newBoxBeforeRotation.y < limitsPositionBeforeRotation.y) {
        newBoxHeight =
          newBoxHeight -
          (limitsPositionBeforeRotation.y - newBoxBeforeRotation.y);
        newBoxBeforeRotation.y = limitsPositionBeforeRotation.y;
      }
      if (
        newBoxWidth >
        limitsWidth - (newBoxBeforeRotation.x - limitsPositionBeforeRotation.x)
      ) {
        newBoxWidth =
          limitsWidth -
          (newBoxBeforeRotation.x - limitsPositionBeforeRotation.x);
      }
      if (
        newBoxHeight >
        limitsHeight - (newBoxBeforeRotation.y - limitsPositionBeforeRotation.y)
      ) {
        newBoxHeight =
          limitsHeight -
          (newBoxBeforeRotation.y - limitsPositionBeforeRotation.y);
      }
      const newBoxRotated = rotatePoint(
        { x: newBoxBeforeRotation.x, y: newBoxBeforeRotation.y },
        rotationCenter,
        props.element.attributes.rotation ?? 0
      );
      newBox.x = newBoxRotated.x;
      newBox.y = newBoxRotated.y;
      newBox.width = newBoxWidth;
      newBox.height = newBoxHeight;
      return newBox;
    }
  };

  const saveCrop = () => {
    const node = cropRectRef.current;
    const nodeBgImage = cropBgImageRef.current;
    if (node && nodeBgImage) {
      const scaleX = node!.scaleX();
      const scaleY = node!.scaleY();

      const newWidth = Math.ceil(Math.max(5, node!.width() * scaleX));
      const newHeight = Math.ceil(Math.max(node!.height() * scaleY));

      const rotation = node.rotation();

      const newPosition = node.position();
      const newCenterPoint = getCenterWithWidthAndHeight(
        newPosition.x,
        newPosition.y,
        newWidth,
        newHeight,
        rotation
      );

      const oldBgPosition = nodeBgImage.position();
      const newBgPosition = getXYBeforeRotation(
        oldBgPosition,
        nodeBgImage.width(),
        nodeBgImage.height(),
        rotation,
        newCenterPoint
      );

      const resizeValues: IResizeValues = {
        x: newPosition.x,
        y: newPosition.y,
        rotation: rotation ? rotation : undefined,
        width: newWidth,
        height: newHeight,
        image: {
          x: newBgPosition.x,
          y: newBgPosition.y,
          width: nodeBgImage.width(),
          height: nodeBgImage.height(),
        },
      };

      props.onCommandChange("resize", resizeValues);

      node.scaleX(1);
      node.scaleY(1);
      node.size({ width: newWidth, height: newHeight });
    }
  };

  const transform = () => {
    const node = imageRef.current;
    const activeAnchor = trRef.current?.getActiveAnchor();
    const rotation = node?.rotation();
    if (node && activeAnchor === "rotater") {
      let stageX = trRef.current?.getStage()?.x();
      let stageY = trRef.current?.getStage()?.y();
      let container = trRef.current?.getStage()?.container() as HTMLDivElement;
      let offsetX = container.offsetLeft + (stageX ?? 0);
      let offsetY = container.offsetTop + (stageY ?? 0);
      props.onRotation(
        rotation,
        node.x() - 50,
        node.y() - 50,
        offsetX,
        offsetY
      );
    }
  };

  const transformEnd = (dragEnd: boolean) => {
    const node = imageRef.current;
    if (node) {
      const scaleX = node!.scaleX();
      const scaleY = node!.scaleY();

      const oldRotation = props.element.attributes.rotation ?? 0;
      const newRotation = node.rotation() ?? 0;

      const oldX = props.element.attributes.x;
      const oldY = props.element.attributes.y;
      const newX = node.x();
      const newY = node.y();
      const oldWidth = node!.width();
      const oldHeight = node!.height();
      const newWidth = Math.ceil(Math.max(5, node!.width() * scaleX));
      const newHeight = Math.ceil(Math.max(node!.height() * scaleY));
      const oldRotationCenter = getCenterWithWidthAndHeight(
        oldX,
        oldY,
        oldWidth,
        oldHeight,
        oldRotation
      );
      const newRotationCenter = getCenterWithWidthAndHeight(
        newX,
        newY,
        newWidth,
        newHeight,
        newRotation
      );
      const oldBgX = props.element.attributes.image.x;
      const oldBgY = props.element.attributes.image.y;
      const oldBgWidth = props.element.attributes.image.width;
      const oldBgHeight = props.element.attributes.image.height;
      const newBgWidth = Math.ceil(oldBgWidth * scaleX);
      const newBgHeight = Math.ceil(oldBgHeight * scaleY);

      const oldXYNORotated = getXYBeforeRotation(
        { x: oldX, y: oldY },
        oldWidth,
        oldHeight,
        oldRotation,
        oldRotationCenter
      );
      const newXYNORotated = getXYBeforeRotation(
        { x: newX, y: newY },
        newWidth,
        newHeight,
        newRotation,
        newRotationCenter
      );
      const newBgX = (oldBgX - oldXYNORotated.x) * scaleX + newXYNORotated.x;
      const newBgY = (oldBgY - oldXYNORotated.y) * scaleY + newXYNORotated.y;

      let resizeValues: IResizeValues = {
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
        rotation: newRotation ? newRotation : undefined,
        image: {
          x: newBgX,
          y: newBgY,
          width: newBgWidth,
          height: newBgHeight,
        },
      };

      node?.scaleX(1);
      node?.scaleY(1);
      if (dragEnd) props.onDragEnd(imageRef, trRef, resizeValues);
      else props.onResize(resizeValues);
      props.onRotation();
    }
  };

  const renderCrop = (node: Konva.Image) => {
    const rotationPoint = getCenter(node, false);
    const position = positionAndRotateAroundPoint(
      { x: attributes.image.x, y: attributes.image.y },
      node?.rotation() ?? 0,
      rotationPoint
    );
    node.cache();
    return (
      <>
        <Group
          ref={cropMainGroupRef}
          draggable={false}
          name={`crop-${props.element.id}`}
        >
          <KonvaImage
            ref={cropBgImageRef}
            image={imageBG}
            scaleX={1}
            scaleY={1}
            x={position.x}
            y={position.y}
            width={attributes.image.width}
            height={attributes.image.height}
            rotation={attributes.rotation}
            opacity={0.5}
            strokeWidth={1.5}
            stroke={"#49d8de"}
            draggable={false}
          />
          <Group
            ref={cropGroupRef}
            draggable={false}
            visible={!attributes.hidden}
            clipFunc={(ctx: any) => {
              const node = cropRectRef.current;
              if (node) {
                ctx.save();
                ctx.translate(node.x(), node.y());
                ctx.rotate((node.rotation() * Math.PI) / 180);
                ctx.rect(
                  0,
                  0,
                  Math.ceil(node.width() * node.scaleX()),
                  Math.ceil(node.height() * node.scaleY())
                );
                ctx.restore();
              }
            }}
            onDragEnd={(e) => {
              if (!props.selectorActive) tranforImage();
            }}
            onDragStart={(e) => {
              if (e.evt.shiftKey) cropGroupRef.current?.stopDrag();
            }}
            type={"image"}
          >
            <KonvaImage
              ref={cropFrontImageRef}
              image={imageBG}
              scaleX={1}
              scaleY={1}
              x={position.x}
              y={position.y}
              width={attributes.image.width}
              height={attributes.image.height}
              rotation={attributes.rotation}
              opacity={attributes.opacity}
              globalCompositeOperation={getBlendMode(attributes.blendMode)}
              draggable={false}
            />
            <Rect
              ref={cropRectRef}
              scaleX={1}
              scaleY={1}
              x={attributes.x}
              y={attributes.y}
              width={attributes.width}
              height={attributes.height}
              rotation={attributes.rotation}
              onTransformEnd={(e) => {
                saveCrop();
              }}
              draggable={false}
            />
          </Group>
        </Group>
        <Transformer
          ref={cropTrRef}
          listening={!attributes.hidden}
          visible={props.isCropping}
          keepRatio={false}
          borderStrokeWidth={2}
          rotateEnabled={false}
          draggable={false}
          resizeEnabled={
            !props.element.attributes.flip ||
            (props.element.attributes.flip?.substr(0, 1) !== "1" &&
              props.element.attributes.flip?.substr(1, 2) !== "1")
          }
          rotateAnchorOffset={20}
          rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
          boundBoxFunc={(oldBox, newBox) => {
            if (props.isCropping) return cropLimits(newBox);

            if (newBox.width < 9 || newBox.height < 9) {
              return oldBox;
            }
            return newBox;
          }}
          {...cropTransformerAttibutes()}
        />
      </>
    );
  };

    const getTransformerBorderColor = () => {
      if (props.isMultipleSelection && props.isOver) return colors.ORANGE;
      else
      return  showDescribedColor && !(props.isSelected || props.isOver) ? props.describedLayerColor: undefined
    };

  return (
    <>
      <>
        <KonvaImage
          name={`${props.containerId}-object`}
          ref={imageRef}
          listening={!attributes.hidden}
          id={props.element.id}
          locked={attributes.locked}
          visible={!attributes.hidden && !props.isCropping}
          image={undefined}
          scaleX={1}
          scaleY={1}
          draggable={
            !attributes.locked &&
            !props.isSelectDescribe &&
            !props.isMultipleSelection &&
            !props.isDragging &&
            props.editMode
          }
          rotation={attributes.rotation}
          cornerRadius={getCornerRadius(
            attributes.cornerRadius,
            imageRef.current
          )}
          opacity={attributes.opacity}
          globalCompositeOperation={getBlendMode(attributes.blendMode)}
          onDblClick={(e) => {
            if (!props.isSelectDescribe && attributes.locked !== true)
              props.onShowCropImage(true, cropMainGroupRef, cropTrRef);
          }}
          onMouseDown={(e) => {
            if (e.evt.button !== 2) props.onSelect();
          }}
          onDragStart={(e) => {
            if (e.evt.shiftKey) imageRef.current?.stopDrag();
          }}
          onDragMove={() => {
            props.onDrag(imageRef, trRef);
          }}
          onDragEnd={(e) => {
            transformEnd(true);
          }}
          onTransformEnd={(e) => {
            transformEnd(false);
          }}
          onTransform={(e) => {
            transform();
          }}
          onMouseOver={(e) => {
            const locked = props.isSelectorActive && attributes.locked;
            if (!locked) props.onOver(true);
          }}
          onMouseOut={(e) => {
            props.onOver(false);
          }}
        />
        {(props.isSelected || props.isOver || showDescribedColor) && (
          <Transformer
            ref={trRef}
            listening={!attributes.hidden}
            keepRatio={true}
            visible={!props.isCropping}
            borderStroke={getTransformerBorderColor()}
            borderStrokeWidth={props.isOver || showDescribedColor ? 2 : 1}
            rotateEnabled={
              attributes.locked !== true &&
              props.isSelected &&
              !props.isMultipleSelection &&
              !props.isSelectDescribe
            }
            resizeEnabled={
              props.isSelected &&
              !props.isMultipleSelection &&
              !props.isSelectDescribe
            }
            enabledAnchors={
              attributes.locked
                ? []
                : ["top-left", "top-right", "bottom-left", "bottom-right"]
            }
            rotateAnchorOffset={20}
            rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
          >
            <ElementLockedAddIn ref={lockIconRef} />
            <CornersAddIn
              show={props.isSelected && props.isSelectDescribe}
              width={imageRef.current ? imageRef.current.width() : 0}
              height={imageRef.current ? imageRef.current.height() : 0}
              scaleX={
                !imageRef.current
                  ? 1
                  : imageRef.current.getLayer()?.getStage().scaleX() ?? 1
              }
              scaleY={
                !imageRef.current
                  ? 1
                  : imageRef.current.getLayer()?.getStage().scaleY() ?? 1
              }
            />
          </Transformer>
        )}
      </>
      {props.isCropping && imageRef.current && renderCrop(imageRef.current)}
    </>
  );
};

export default ImageCanvasElement;
